import React, { useState, useEffect } from 'react';
import { Upload, Trophy, Check, X, Image as ImageIcon } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const UploadWinner = () => {
  const [winners, setWinners] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    winnerName: '',
    gameName: '',
    prize: '',
    city: '',
    score: '',
    date: '',
    photoUrl: ''
  });
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check if user is admin
    const sessionToken = localStorage.getItem('admin_session_token');
    if (!sessionToken) {
      window.location.href = '/admin/login';
    }
    loadWinners();
  }, []);

  const loadWinners = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/winners/gallery?limit=100`);
      setWinners(response.data.winners || []);
    } catch (error) {
      console.error('Error loading winners:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.winnerName || !formData.photoUrl) {
      toast.error('Please fill Winner Name and Photo URL');
      return;
    }

    setUploading(true);
    
    try {
      // Create a manual winner entry
      const winnerData = {
        winner_id: `manual_${Date.now()}`,
        user_id: 'manual',
        competition_id: `manual_${Date.now()}`,
        game_id: formData.gameName.toLowerCase().replace(/\s+/g, '-'),
        game_name: formData.gameName,
        
        player_name: formData.winnerName,
        player_email: '',
        player_phone: '',
        player_city: formData.city,
        player_state: '',
        
        score: parseInt(formData.score) || 0,
        competition_date: formData.date || new Date().toISOString(),
        prize_name: formData.prize,
        prize_value: 0,
        
        verification_status: 'verified',
        verified_at: new Date().toISOString(),
        verified_by: 'admin',
        verification_notes: 'Manually uploaded',
        
        delivery_address: {},
        alternate_phone: '',
        
        winner_photo_url: formData.photoUrl,
        announcement_image_url: '',
        
        is_announced: true,
        announced_at: new Date().toISOString(),
        announcement_text: `Congratulations ${formData.winnerName} from ${formData.city}!`,
        
        delivery_status: 'pending',
        tracking_number: '',
        shipped_at: null,
        delivered_at: null,
        
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      // Save to database
      await axios.post(`${BACKEND_URL}/api/admin/winner/manual-create`, winnerData);
      
      toast.success('Winner uploaded successfully!');
      
      // Reset form
      setFormData({
        winnerName: '',
        gameName: '',
        prize: '',
        city: '',
        score: '',
        date: '',
        photoUrl: ''
      });
      
      // Reload winners
      loadWinners();
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload winner');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (winnerId) => {
    if (!window.confirm('Delete this winner?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/winner/${winnerId}`);
      toast.success('Winner deleted');
      loadWinners();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-purple-900 to-gray-900 py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-3">
            <Trophy className="w-10 h-10 text-yellow-400" />
            Upload Winner
          </h1>
          <p className="text-gray-400">Add competition winners to the gallery</p>
        </div>

        {/* Upload Form */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-400" />
            Add New Winner
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Winner Name */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  Winner Name *
                </label>
                <input
                  type="text"
                  value={formData.winnerName}
                  onChange={(e) => setFormData({...formData, winnerName: e.target.value})}
                  placeholder="e.g. Rahul Sharma"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                  required
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  placeholder="e.g. Mumbai"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Game Name */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  Game Name
                </label>
                <select
                  value={formData.gameName}
                  onChange={(e) => setFormData({...formData, gameName: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="">Select Game</option>
                  <option value="Tap Challenge">Tap Challenge</option>
                  <option value="Memory Master">Memory Master</option>
                  <option value="Lightning Quiz">Lightning Quiz</option>
                  <option value="Reaction Test">Reaction Test</option>
                  <option value="Flappy Tap">Flappy Tap</option>
                  <option value="Ball Balance">Ball Balance</option>
                  <option value="Target Hit">Target Hit</option>
                  <option value="Perfect Timing">Perfect Timing</option>
                  <option value="Ball Jump">Ball Jump</option>
                  <option value="Bottle Flip">Bottle Flip</option>
                  <option value="Tap Flash">Tap Flash</option>
                </select>
              </div>

              {/* Prize */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  Prize
                </label>
                <input
                  type="text"
                  value={formData.prize}
                  onChange={(e) => setFormData({...formData, prize: e.target.value})}
                  placeholder="e.g. Neck Heater"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Score */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  Score
                </label>
                <input
                  type="number"
                  value={formData.score}
                  onChange={(e) => setFormData({...formData, score: e.target.value})}
                  placeholder="e.g. 4003"
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-gray-300 text-sm font-semibold mb-2">
                  Competition Date
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            </div>

            {/* Photo URL */}
            <div>
              <label className="block text-gray-300 text-sm font-semibold mb-2">
                Winner Photo URL *
              </label>
              <input
                type="url"
                value={formData.photoUrl}
                onChange={(e) => setFormData({...formData, photoUrl: e.target.value})}
                placeholder="https://example.com/winner-photo.jpg"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-blue-500"
                required
              />
              <p className="text-gray-500 text-xs mt-1">
                Upload photo to image hosting service (imgur, cloudinary, etc.) and paste URL here
              </p>
            </div>

            {/* Preview */}
            {formData.photoUrl && (
              <div className="bg-gray-700/50 rounded-lg p-4">
                <p className="text-gray-300 text-sm mb-2">Preview:</p>
                <img 
                  src={formData.photoUrl} 
                  alt="Preview"
                  className="w-32 h-32 object-cover rounded-lg border-2 border-yellow-500"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    toast.error('Invalid image URL');
                  }}
                />
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={uploading}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 rounded-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Upload Winner
                </>
              )}
            </button>
          </form>
        </div>

        {/* Winners List */}
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-yellow-400" />
            Uploaded Winners ({winners.length})
          </h2>

          {winners.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Trophy className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No winners uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {winners.map((winner) => (
                <div 
                  key={winner.winner_id}
                  className="bg-gray-700 rounded-lg overflow-hidden border border-gray-600 hover:border-yellow-500 transition-all"
                >
                  {/* Photo */}
                  <div className="aspect-square bg-gray-900 flex items-center justify-center">
                    {winner.winner_photo_url ? (
                      <img 
                        src={winner.winner_photo_url} 
                        alt={winner.player_name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Trophy className="w-12 h-12 text-gray-600" />
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3">
                    <h3 className="text-white font-bold truncate">{winner.player_name}</h3>
                    <p className="text-gray-400 text-sm">{winner.player_city}</p>
                    <p className="text-gray-500 text-xs mt-1">{winner.game_name}</p>
                    <p className="text-yellow-400 text-xs">{winner.prize_name}</p>
                    
                    <button
                      onClick={() => handleDelete(winner.winner_id)}
                      className="mt-2 text-red-400 hover:text-red-300 text-xs flex items-center gap-1"
                    >
                      <X className="w-3 h-3" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-8 bg-blue-900/20 border border-blue-700 rounded-lg p-4">
          <h3 className="text-white font-semibold mb-2">📸 How to Upload Winner Photos:</h3>
          <ol className="text-gray-300 text-sm space-y-1 list-decimal list-inside">
            <li>Take a photo of the winner (with permission)</li>
            <li>Upload to image hosting: <a href="https://imgur.com/upload" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Imgur</a> or <a href="https://cloudinary.com" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">Cloudinary</a></li>
            <li>Copy the direct image URL</li>
            <li>Paste URL in the form above</li>
            <li>Fill in other details and click Upload</li>
          </ol>
        </div>

      </div>
    </div>
  );
};

export default UploadWinner;
